"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { GlobalSearchTrigger } from "./global-search";
import {
  LayoutDashboard,
  Radio,
  Film,
  Eye,
  ListOrdered,
  Scissors,
  Calendar,
  Compass,
  Clapperboard,
  Shapes,
  Mic,
  CalendarClock,
  Plug,
  Settings,
  Factory,
  BarChart3,
  Menu,
  X,
  LogOut,
  type LucideIcon,
} from "lucide-react";

const NAV_ITEMS: { href: string; label: string; icon: LucideIcon }[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/channels", label: "Channels", icon: Radio },
  { href: "/videos", label: "Videos", icon: Film },
  { href: "/review", label: "Review", icon: Eye },
  { href: "/queue", label: "Queue", icon: ListOrdered },
  { href: "/shorts", label: "Shorts", icon: Scissors },
  { href: "/calendar", label: "Calendar", icon: Calendar },
  { href: "/discovery", label: "Discovery", icon: Compass },
  { href: "/production", label: "Production", icon: Clapperboard },
  { href: "/analytics", label: "Analytics", icon: BarChart3 },
  { href: "/scene-types", label: "Scene Types", icon: Shapes },
  { href: "/voices", label: "Voices", icon: Mic },
  { href: "/schedule", label: "Schedule", icon: CalendarClock },
  { href: "/integrations", label: "Integrations", icon: Plug },
  { href: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex h-14 items-center justify-between border-b border-zinc-800 bg-zinc-950 px-4">
        <div className="flex items-center gap-2">
          <Factory className="h-5 w-5 text-zinc-400" />
          <span className="text-base font-bold tracking-tight text-zinc-100">
            Video Factory
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="rounded-md p-2 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-56 flex-col border-r border-zinc-800 bg-zinc-950 transition-transform duration-200 ease-out",
          "md:translate-x-0 md:pointer-events-auto",
          open ? "translate-x-0 pointer-events-auto" : "-translate-x-full pointer-events-none"
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-zinc-800 px-4">
          <div className="flex items-center gap-2">
            <Factory className="h-5 w-5 text-zinc-400" />
            <span className="text-lg font-bold tracking-tight text-zinc-100">
              Video Factory
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-2 pt-3">
          <GlobalSearchTrigger />
        </div>
        <nav className="flex-1 space-y-1 px-2 py-3">
          {NAV_ITEMS.map((item) => {
            const isActive =
              pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-zinc-800 text-zinc-100"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                )}
              >
                <item.icon className="h-4 w-4 opacity-60" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-zinc-800 px-2 py-3 space-y-2">
          <button
            onClick={async () => {
              await fetch("/api/auth/logout", { method: "POST" });
              window.location.href = "/login";
            }}
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300 transition-colors w-full"
          >
            <LogOut className="h-4 w-4 opacity-60" />
            Sign out
          </button>
          <p className="text-xs text-zinc-600 px-3">Port 3650</p>
        </div>
      </aside>
    </>
  );
}
